import React, { useState } from 'react';
import { userAPI } from '../../services/api';
import styles from './ChangePassword.module.css';

const ChangePassword = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // 清除对应字段的错误信息
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.oldPassword.trim()) {
      newErrors.oldPassword = '请输入原密码';
    }

    if (!formData.newPassword.trim()) {
      newErrors.newPassword = '请输入新密码';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = '新密码长度至少6个字符';
    } else if (formData.newPassword.length > 20) {
      newErrors.newPassword = '新密码长度不能超过20个字符';
    }

    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = '请确认新密码';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = '两次输入的密码不一致';
    }

    if (formData.oldPassword === formData.newPassword) {
      newErrors.newPassword = '新密码不能与原密码相同';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const response = await userAPI.changePassword(formData);

      if (response.data?.code === 200) {
        // 设置成功状态
        setSuccess(true);
        // 先显示成功消息
        onSuccess?.('密码修改成功，请妥善保管新密码');

        // 缩短延迟时间，让用户更快回到个人中心
        setTimeout(() => {
          onClose?.();
        }, 1500);
      } else {
        setErrors({ general: response.data?.message || '密码修改失败' });
      }
    } catch (error) {
      console.error('修改密码失败:', error);
      const errorMessage = error.response?.data?.message || '密码修改失败，请稍后重试';
      setErrors({ general: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = (password) => {
    if (!password) return { level: 0, text: '', color: '#e0e0e0' };

    let score = 0;
    if (password.length >= 8) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^a-zA-Z\d]/.test(password)) score++;

    const levels = [
      { level: 0, text: '', color: '#e0e0e0' },
      { level: 1, text: '弱', color: '#ff4444' },
      { level: 2, text: '较弱', color: '#ff8800' },
      { level: 3, text: '中等', color: '#ffbb00' },
      { level: 4, text: '强', color: '#88cc00' },
      { level: 5, text: '很强', color: '#00cc44' }
    ];

    return levels[score];
  };

  const passwordStrength = getPasswordStrength(formData.newPassword);

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2>修改密码</h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="关闭"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {success && (
            <div className={styles.successAlert}>
              <div className={styles.successIcon}>✅</div>
              <div>
                <strong>密码修改成功！</strong>
                <p>您的密码已安全更新，页面即将关闭</p>
              </div>
            </div>
          )}

          {errors.general && !success && (
            <div className={styles.errorAlert}>
              {errors.general}
            </div>
          )}

          <div className={styles.formGroup}>
            <label htmlFor="oldPassword" className={styles.label}>
              原密码
            </label>
            <input
              type="password"
              id="oldPassword"
              name="oldPassword"
              value={formData.oldPassword}
              onChange={handleInputChange}
              className={`${styles.input} ${errors.oldPassword ? styles.inputError : ''}`}
              placeholder="请输入原密码"
              disabled={loading || success}
            />
            {errors.oldPassword && (
              <div className={styles.fieldError}>{errors.oldPassword}</div>
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="newPassword" className={styles.label}>
              新密码
            </label>
            <input
              type="password"
              id="newPassword"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleInputChange}
              className={`${styles.input} ${errors.newPassword ? styles.inputError : ''}`}
              placeholder="请输入新密码（6-20个字符）"
              disabled={loading || success}
            />
            {formData.newPassword && (
              <div className={styles.passwordStrength}>
                <div className={styles.strengthBar}>
                  <div
                    className={styles.strengthFill}
                    style={{
                      width: `${(passwordStrength.level / 5) * 100}%`,
                      backgroundColor: passwordStrength.color
                    }}
                  ></div>
                </div>
                <span
                  className={styles.strengthText}
                  style={{ color: passwordStrength.color }}
                >
                  密码强度：{passwordStrength.text}
                </span>
              </div>
            )}
            {errors.newPassword && (
              <div className={styles.fieldError}>{errors.newPassword}</div>
            )}
            <div className={styles.passwordTips}>
              <p>密码要求：</p>
              <ul>
                <li>长度6-20个字符</li>
                <li>建议包含字母、数字和特殊字符</li>
                <li>避免使用个人信息</li>
              </ul>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="confirmPassword" className={styles.label}>
              确认新密码
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className={`${styles.input} ${errors.confirmPassword ? styles.inputError : ''}`}
              placeholder="请再次输入新密码"
              disabled={loading || success}
            />
            {errors.confirmPassword && (
              <div className={styles.fieldError}>{errors.confirmPassword}</div>
            )}
          </div>

          <div className={styles.buttonGroup}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onClose}
              disabled={loading}
            >
              {success ? '关闭' : '取消'}
            </button>
            <button
              type="submit"
              className={`${styles.submitButton} ${loading ? styles.loading : ''}`}
              disabled={loading || success}
            >
              {loading ? (
                <>
                  <div className={styles.spinner}></div>
                  修改中...
                </>
              ) : success ? (
                '修改成功'
              ) : (
                '确认修改'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;